<template>
  <div>
    <vx-card title="مدیریت بسته‌های ترافیک" subtitle="تنظیم بسته‌های ترافیک اضافه و قیمت‌گذاری منطقه‌ای">
      <!-- Tabs -->
      <div class="flex items-center mb-6 border-b">
        <button
          class="px-4 py-2 font-semibold transition-colors"
          :class="activeTab === 'packages' ? 'text-primary border-b-2 border-primary' : 'text-gray-500'"
          @click="activeTab = 'packages'"
        >
          <i class="bx bx-package mr-1"></i>
          بسته‌ها
        </button>
        <button
          class="px-4 py-2 font-semibold transition-colors"
          :class="activeTab === 'matrix' ? 'text-primary border-b-2 border-primary' : 'text-gray-500'"
          @click="activeTab = 'matrix'; loadMatrix()"
        >
          <i class="bx bx-grid-alt mr-1"></i>
          قیمت‌گذاری منطقه‌ای
        </button>
        <button
          class="px-4 py-2 font-semibold transition-colors"
          :class="activeTab === 'stats' ? 'text-primary border-b-2 border-primary' : 'text-gray-500'"
          @click="activeTab = 'stats'; loadStats()"
        >
          <i class="bx bx-chart mr-1"></i>
          آمار فروش
        </button>
      </div>

      <!-- Packages Tab -->
      <div v-show="activeTab === 'packages'">
        <div class="flex justify-between items-center mb-4">
          <vs-button @click="openCreateModal" color="primary">
            <i class="bx bx-plus mr-1"></i>
            افزودن بسته جدید
          </vs-button>
          <vs-button @click="loadPackages" color="dark" type="border">
            <i class="bx bx-refresh mr-1"></i>
            بارگذاری مجدد
          </vs-button>
        </div>

        <div v-if="loading" class="flex justify-center py-12">
          <vs-progress indeterminate color="primary">در حال بارگذاری...</vs-progress>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr>
                <th class="px-4 py-3 text-right bg-gray-100 dark:bg-gray-800 border-b">نام</th>
                <th class="px-4 py-3 text-right bg-gray-100 dark:bg-gray-800 border-b">حجم ترافیک</th>
                <th class="px-4 py-3 text-right bg-gray-100 dark:bg-gray-800 border-b">قیمت پایه</th>
                <th class="px-4 py-3 text-right bg-gray-100 dark:bg-gray-800 border-b">قیمت هر GB</th>
                <th class="px-4 py-3 text-center bg-gray-100 dark:bg-gray-800 border-b">وضعیت</th>
                <th class="px-4 py-3 text-center bg-gray-100 dark:bg-gray-800 border-b">ترتیب</th>
                <th class="px-4 py-3 text-center bg-gray-100 dark:bg-gray-800 border-b">عملیات</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="pkg in packages"
                :key="pkg.id"
                class="border-b hover:bg-gray-50 dark:hover:bg-gray-900"
              >
                <td class="px-4 py-3">
                  <div class="font-semibold">{{ pkg.name }}</div>
                  <div class="text-sm text-gray-500">{{ pkg.name_fa }}</div>
                </td>
                <td class="px-4 py-3" dir="ltr">{{ pkg.traffic_gb }} GB</td>
                <td class="px-4 py-3">{{ formatPrice(pkg.base_price) }}</td>
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-500">
                    {{ formatPrice(Math.round(pkg.base_price / pkg.traffic_gb)) }}/GB
                  </span>
                </td>
                <td class="px-4 py-3 text-center">
                  <vs-switch
                    :value="pkg.is_active"
                    @input="toggleActive(pkg)"
                  />
                </td>
                <td class="px-4 py-3 text-center">
                  {{ pkg.sort_order }}
                </td>
                <td class="px-4 py-3 text-center">
                  <vs-button size="small" color="primary" type="border" @click="openEditModal(pkg)">
                    <i class="bx bx-edit"></i>
                  </vs-button>
                  <vs-button size="small" color="danger" type="border" class="mr-2" @click="confirmDelete(pkg)">
                    <i class="bx bx-trash"></i>
                  </vs-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!loading && packages.length === 0" class="text-center py-12">
          <i class="bx bx-package text-6xl text-gray-400 mb-4"></i>
          <p class="text-gray-500">هیچ بسته ترافیکی یافت نشد</p>
        </div>
      </div>

      <!-- Matrix Tab -->
      <div v-show="activeTab === 'matrix'">
        <div class="flex items-end mb-4">
          <vs-select
            v-model="selectedRegion"
            placeholder="فیلتر بر اساس منطقه"
            class="w-full md:w-1/3 ml-4"
          >
            <vs-select-item value="all" text="همه مناطق" />
            <vs-select-item
              v-for="region in regions"
              :key="region.id"
              :value="region.id"
              :text="`${region.name} (${region.country_code})`"
            />
          </vs-select>
          <vs-button @click="loadMatrix" color="primary">
            <i class="bx bx-refresh"></i>
            بارگذاری مجدد
          </vs-button>
        </div>

        <div v-if="matrixLoading" class="flex justify-center py-12">
          <vs-progress indeterminate color="primary">در حال بارگذاری...</vs-progress>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr>
                <th class="px-4 py-3 text-right bg-gray-100 dark:bg-gray-800 border-b">
                  منطقه / بسته
                </th>
                <th
                  v-for="pkg in matrixPackages"
                  :key="pkg.id"
                  class="px-4 py-3 text-center bg-gray-100 dark:bg-gray-800 border-b min-w-40"
                >
                  <div class="font-semibold text-sm">{{ pkg.name }}</div>
                  <div class="text-xs text-gray-500" dir="ltr">{{ pkg.traffic_gb }} GB</div>
                  <div class="text-xs text-gray-400">پایه: {{ formatPrice(pkg.base_price) }}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in filteredMatrix"
                :key="row.region.id"
                class="border-b hover:bg-gray-50 dark:hover:bg-gray-900"
              >
                <td class="px-4 py-3 font-semibold">
                  <div class="flex items-center">
                    <span class="flag-icon ml-2">{{ getFlagEmoji(row.region.country_code) }}</span>
                    <div>
                      <div>{{ row.region.name }}</div>
                      <div class="text-xs text-gray-500">{{ row.region.city }}</div>
                    </div>
                  </div>
                </td>
                <td
                  v-for="pkgData in row.packages"
                  :key="pkgData.package.id"
                  class="px-4 py-3 text-center"
                  :class="{
                    'bg-green-50 dark:bg-green-900': pkgData.is_available,
                    'bg-gray-100 dark:bg-gray-800 opacity-50': !pkgData.is_available
                  }"
                >
                  <div class="flex flex-col items-center">
                    <!-- Availability Toggle -->
                    <vs-switch
                      :value="pkgData.is_available"
                      @input="togglePackageAvailability(row.region.id, pkgData.package.id, $event)"
                      class="mb-2"
                    >
                      <template #off>غیرفعال</template>
                      <template #on>فعال</template>
                    </vs-switch>

                    <!-- Price Override -->
                    <div v-if="pkgData.is_available" class="mt-2 w-full">
                      <vs-input
                        :value="pkgData.price_override"
                        :placeholder="`پایه: ${formatPrice(pkgData.package.base_price)}`"
                        type="number"
                        min="0"
                        size="small"
                        class="w-full"
                        @blur="updatePriceOverride(row.region.id, pkgData.package.id, $event.target.value)"
                      >
                        <template #icon>
                          <i class="bx bx-dollar-circle"></i>
                        </template>
                      </vs-input>
                      <div class="text-xs text-gray-500 mt-1">
                        <span v-if="pkgData.price_override" class="text-primary font-semibold">
                          سفارشی: {{ formatPrice(pkgData.price_override) }}
                        </span>
                        <span v-else>
                          قیمت پایه
                        </span>
                      </div>
                      <div class="text-xs text-gray-400 mt-1" dir="ltr">
                        {{ formatPrice(pkgData.effective_price / pkgData.package.traffic_gb) }}/GB
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!matrixLoading && matrix.length === 0" class="text-center py-12">
          <i class="bx bx-grid-alt text-6xl text-gray-400 mb-4"></i>
          <p class="text-gray-500">داده‌ای برای نمایش وجود ندارد</p>
        </div>
      </div>

      <!-- Stats Tab -->
      <div v-show="activeTab === 'stats'">
        <div v-if="statsLoading" class="flex justify-center py-12">
          <vs-progress indeterminate color="primary">در حال بارگذاری...</vs-progress>
        </div>

        <div v-else-if="stats" class="space-y-6">
          <!-- Summary Cards Row 1 -->
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
              <div class="text-sm opacity-75">کل فروش</div>
              <div class="text-2xl font-bold">{{ stats.total_sales }}</div>
            </div>
            <div class="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
              <div class="text-sm opacity-75">درآمد کل</div>
              <div class="text-2xl font-bold">{{ formatPrice(stats.total_revenue) }}</div>
            </div>
            <div class="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
              <div class="text-sm opacity-75">ترافیک فروخته شده</div>
              <div class="text-2xl font-bold" dir="ltr">{{ stats.total_traffic_sold_gb }} GB</div>
            </div>
            <div class="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 text-white">
              <div class="text-sm opacity-75">اتصال‌های فعال</div>
              <div class="text-2xl font-bold">{{ stats.active_addons }}</div>
            </div>
          </div>

          <!-- Summary Cards Row 2 -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg p-4 text-white">
              <div class="text-sm opacity-75">ترافیک مصرف شده</div>
              <div class="text-2xl font-bold" dir="ltr">{{ stats.total_traffic_used_gb }} GB</div>
              <div v-if="stats.total_traffic_sold_gb > 0" class="text-xs opacity-75 mt-1">
                {{ Math.round(stats.total_traffic_used_gb / stats.total_traffic_sold_gb * 100) }}% مصرف شده
              </div>
            </div>
            <div class="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg p-4 text-white">
              <div class="text-sm opacity-75">کل بسته‌ها</div>
              <div class="text-2xl font-bold">{{ stats.total_packages }}</div>
              <div class="text-xs opacity-75 mt-1">{{ stats.active_packages }} بسته فعال</div>
            </div>
            <div class="bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg p-4 text-white">
              <div class="text-sm opacity-75">میانگین درآمد هر فروش</div>
              <div class="text-2xl font-bold">
                {{ stats.total_sales > 0 ? formatPrice(Math.round(stats.total_revenue / stats.total_sales)) : 0 }}
              </div>
            </div>
          </div>

          <!-- Sales by Package -->
          <div class="bg-white dark:bg-gray-800 rounded-lg p-4">
            <h4 class="font-semibold mb-4">فروش به تفکیک بسته</h4>
            <table class="w-full">
              <thead>
                <tr>
                  <th class="text-right py-2 border-b">بسته</th>
                  <th class="text-center py-2 border-b">حجم</th>
                  <th class="text-center py-2 border-b">تعداد فروش</th>
                  <th class="text-center py-2 border-b">ترافیک فروخته شده</th>
                  <th class="text-center py-2 border-b">درآمد</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in stats.sales_by_package" :key="item.package_id" class="border-b hover:bg-gray-50 dark:hover:bg-gray-900">
                  <td class="py-2">
                    <div class="font-semibold">{{ item.package && item.package.name_fa ? item.package.name_fa : item.package_id }}</div>
                    <div class="text-xs text-gray-400">{{ item.package && item.package.name }}</div>
                  </td>
                  <td class="py-2 text-center" dir="ltr">{{ item.package && item.package.traffic_gb ? item.package.traffic_gb + ' GB' : '-' }}</td>
                  <td class="py-2 text-center">{{ item.count }}</td>
                  <td class="py-2 text-center" dir="ltr">{{ item.traffic_gb }} GB</td>
                  <td class="py-2 text-center">{{ formatPrice(item.revenue) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="!stats.sales_by_package || stats.sales_by_package.length === 0" class="text-center py-6 text-gray-400">
              هیچ فروشی ثبت نشده
            </div>
          </div>

          <!-- Sales by Region -->
          <div class="bg-white dark:bg-gray-800 rounded-lg p-4">
            <h4 class="font-semibold mb-4">فروش به تفکیک منطقه</h4>
            <table class="w-full">
              <thead>
                <tr>
                  <th class="text-right py-2 border-b">منطقه</th>
                  <th class="text-center py-2 border-b">تعداد فروش</th>
                  <th class="text-center py-2 border-b">درآمد</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in stats.sales_by_region" :key="item.region_id" class="border-b hover:bg-gray-50 dark:hover:bg-gray-900">
                  <td class="py-2">{{ item.region && item.region.name ? item.region.name : item.region_id }}</td>
                  <td class="py-2 text-center">{{ item.count }}</td>
                  <td class="py-2 text-center">{{ formatPrice(item.revenue) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="!stats.sales_by_region || stats.sales_by_region.length === 0" class="text-center py-6 text-gray-400">
              هیچ فروشی ثبت نشده
            </div>
          </div>
        </div>
      </div>
    </vx-card>

    <!-- Create/Edit Modal -->
    <vs-popup :active.sync="showModal" :title="isEditing ? 'ویرایش بسته' : 'افزودن بسته جدید'">
      <div class="space-y-4">
        <vs-input
          v-model="form.name"
          label="نام (انگلیسی)"
          placeholder="e.g. Traffic 100GB"
          class="w-full"
        />
        <vs-input
          v-model="form.name_fa"
          label="نام (فارسی)"
          placeholder="مثال: بسته ترافیک ۱۰۰ گیگ"
          class="w-full"
        />
        <vs-input
          v-model.number="form.traffic_gb"
          label="حجم ترافیک (GB)"
          type="number"
          min="1"
          class="w-full"
        />
        <vs-input
          v-model.number="form.base_price"
          label="قیمت پایه (تومان)"
          type="number"
          min="0"
          class="w-full"
        />
        <vs-textarea
          v-model="form.description"
          label="توضیحات (انگلیسی)"
          class="w-full"
        />
        <vs-textarea
          v-model="form.description_fa"
          label="توضیحات (فارسی)"
          class="w-full"
        />
        <vs-input
          v-model.number="form.sort_order"
          label="ترتیب نمایش"
          type="number"
          min="0"
          class="w-full"
        />
        <div class="flex items-center">
          <vs-switch v-model="form.is_active" />
          <span class="mr-2">فعال</span>
        </div>
      </div>

      <div class="flex justify-end mt-6 gap-2">
        <vs-button @click="showModal = false" type="border">انصراف</vs-button>
        <vs-button @click="savePackage" color="primary" :disabled="saving">
          {{ saving ? 'در حال ذخیره...' : 'ذخیره' }}
        </vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: 'HaioFlashTrafficPackages',
  data() {
    return {
      activeTab: 'packages',
      loading: false,
      packages: [],
      
      // Matrix
      matrixLoading: false,
      regions: [],
      matrixPackages: [],
      matrix: [],
      selectedRegion: 'all',
      
      // Stats
      statsLoading: false,
      stats: null,
      
      // Modal
      showModal: false,
      isEditing: false,
      editingId: null,
      saving: false,
      form: {
        name: '',
        name_fa: '',
        traffic_gb: 100,
        base_price: 50000,
        description: '',
        description_fa: '',
        sort_order: 0,
        is_active: true,
      },
    }
  },
  computed: {
    filteredMatrix() {
      if (this.selectedRegion === 'all') {
        return this.matrix
      }
      return this.matrix.filter(row => row.region.id === this.selectedRegion)
    }
  },
  mounted() {
    this.loadPackages()
  },
  methods: {
    formatPrice(price) {
      if (!price && price !== 0) return '—'
      return new Intl.NumberFormat('fa-IR').format(price) + ' تومان'
    },
    
    getFlagEmoji(countryCode) {
      if (!countryCode) return '🌐'
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0))
      return String.fromCodePoint(...codePoints)
    },

    async loadPackages() {
      this.loading = true
      try {
        const response = await this.$http.get('/admin/haioflash/traffic-packages')
        if (response.data.success) {
          this.packages = response.data.data
        }
      } catch (error) {
        console.error('Error loading packages:', error)
        this.$vs.notify({
          title: 'خطا',
          text: 'خطا در بارگذاری بسته‌ها',
          color: 'danger',
        })
      } finally {
        this.loading = false
      }
    },

    async loadMatrix() {
      this.matrixLoading = true
      try {
        const response = await this.$http.get('/admin/haioflash/traffic-packages/matrix')
        if (response.data.success) {
          this.regions = response.data.data.regions
          this.matrixPackages = response.data.data.packages
          this.matrix = response.data.data.matrix
        }
      } catch (error) {
        console.error('Error loading matrix:', error)
        this.$vs.notify({
          title: 'خطا',
          text: 'خطا در بارگذاری ماتریس قیمت‌گذاری',
          color: 'danger',
        })
      } finally {
        this.matrixLoading = false
      }
    },

    async loadStats() {
      this.statsLoading = true
      try {
        const response = await this.$http.get('/admin/haioflash/traffic-packages/stats')
        if (response.data.success) {
          this.stats = response.data.data
        }
      } catch (error) {
        console.error('Error loading stats:', error)
        this.$vs.notify({
          title: 'خطا',
          text: 'خطا در بارگذاری آمار',
          color: 'danger',
        })
      } finally {
        this.statsLoading = false
      }
    },

    openCreateModal() {
      this.isEditing = false
      this.editingId = null
      this.form = {
        name: '',
        name_fa: '',
        traffic_gb: 100,
        base_price: 50000,
        description: '',
        description_fa: '',
        sort_order: 0,
        is_active: true,
      }
      this.showModal = true
    },

    openEditModal(pkg) {
      this.isEditing = true
      this.editingId = pkg.id
      this.form = {
        name: pkg.name,
        name_fa: pkg.name_fa,
        traffic_gb: pkg.traffic_gb,
        base_price: pkg.base_price,
        description: pkg.description || '',
        description_fa: pkg.description_fa || '',
        sort_order: pkg.sort_order || 0,
        is_active: pkg.is_active,
      }
      this.showModal = true
    },

    async savePackage() {
      this.saving = true
      try {
        let response
        if (this.isEditing) {
          response = await this.$http.put(`/admin/haioflash/traffic-packages/${this.editingId}`, this.form)
        } else {
          response = await this.$http.post('/admin/haioflash/traffic-packages', this.form)
        }

        if (response.data.success) {
          this.$vs.notify({
            title: 'موفق',
            text: this.isEditing ? 'بسته با موفقیت ویرایش شد' : 'بسته با موفقیت ایجاد شد',
            color: 'success',
          })
          this.showModal = false
          this.loadPackages()
        }
      } catch (error) {
        console.error('Error saving package:', error)
        this.$vs.notify({
          title: 'خطا',
          text: (error.response && error.response.data && error.response.data.message) || 'خطا در ذخیره بسته',
          color: 'danger',
        })
      } finally {
        this.saving = false
      }
    },

    async toggleActive(pkg) {
      try {
        const response = await this.$http.put(`/admin/haioflash/traffic-packages/${pkg.id}`, {
          ...pkg,
          is_active: !pkg.is_active,
        })
        if (response.data.success) {
          pkg.is_active = !pkg.is_active
          this.$vs.notify({
            title: 'موفق',
            text: 'وضعیت بسته تغییر کرد',
            color: 'success',
          })
        }
      } catch (error) {
        console.error('Error toggling active:', error)
        this.$vs.notify({
          title: 'خطا',
          text: 'خطا در تغییر وضعیت',
          color: 'danger',
        })
      }
    },

    confirmDelete(pkg) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'تأیید حذف',
        text: `آیا از حذف بسته "${pkg.name}" اطمینان دارید؟`,
        accept: () => this.deletePackage(pkg),
      })
    },

    async deletePackage(pkg) {
      try {
        const response = await this.$http.delete(`/admin/haioflash/traffic-packages/${pkg.id}`)
        if (response.data.success) {
          this.$vs.notify({
            title: 'موفق',
            text: 'بسته با موفقیت حذف شد',
            color: 'success',
          })
          this.loadPackages()
        }
      } catch (error) {
        console.error('Error deleting package:', error)
        this.$vs.notify({
          title: 'خطا',
          text: (error.response && error.response.data && error.response.data.message) || 'خطا در حذف بسته',
          color: 'danger',
        })
      }
    },

    async togglePackageAvailability(regionId, packageId, isAvailable) {
      try {
        const response = await this.$http.post(`/admin/haioflash/traffic-packages/${packageId}/region-pricing`, {
          region_id: regionId,
          is_available: isAvailable,
        })
        if (response.data.success) {
          // Update local data
          const row = this.matrix.find(r => r.region.id === regionId)
          if (row) {
            const pkgData = row.packages.find(p => p.package.id === packageId)
            if (pkgData) {
              pkgData.is_available = isAvailable
            }
          }
          this.$vs.notify({
            title: 'موفق',
            text: 'وضعیت در دسترس بودن تغییر کرد',
            color: 'success',
          })
        }
      } catch (error) {
        console.error('Error updating availability:', error)
        this.$vs.notify({
          title: 'خطا',
          text: 'خطا در تغییر وضعیت',
          color: 'danger',
        })
        this.loadMatrix()
      }
    },

    async updatePriceOverride(regionId, packageId, priceOverride) {
      try {
        const price = priceOverride === '' || priceOverride === null ? null : parseInt(priceOverride)
        
        const response = await this.$http.post(`/admin/haioflash/traffic-packages/${packageId}/region-pricing`, {
          region_id: regionId,
          price_override: price,
          is_available: true,
        })

        if (response.data.success) {
          // Update local data
          const row = this.matrix.find(r => r.region.id === regionId)
          if (row) {
            const pkgData = row.packages.find(p => p.package.id === packageId)
            if (pkgData) {
              pkgData.price_override = price
              pkgData.effective_price = price || pkgData.package.base_price
            }
          }
          this.$vs.notify({
            title: 'موفق',
            text: 'قیمت سفارشی ذخیره شد',
            color: 'success',
          })
        }
      } catch (error) {
        console.error('Error updating price:', error)
        this.$vs.notify({
          title: 'خطا',
          text: 'خطا در ذخیره قیمت',
          color: 'danger',
        })
        this.loadMatrix()
      }
    },
  },
}
</script>

<style scoped>
.min-w-40 {
  min-width: 10rem;
}
</style>
